import { useState, useEffect, useCallback } from "react";
import { Newspaper, ExternalLink, Search, Loader2, Heart, MessageCircle, Send, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getVisitorId } from "../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["전체", "연애·관계", "재혼·동반자", "싱글라이프", "사회·문화", "해외 트렌드"];
const PAGE_SIZE = 20;

function fmtNewsDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

function NewsSocial({ article }) {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [likes, setLikes] = useState(article.likes || 0);
  const [liked, setLiked] = useState(!!article.liked);
  const [liking, setLiking] = useState(false);
  const [commentCount, setCommentCount] = useState(article.comment_count || 0);
  const [comments, setComments] = useState(null);
  const [content, setContent] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const loadComments = async () => {
    try {
      const r = await fetch(`${API_URL}/api/news/${article.id}/social?visitor_id=${encodeURIComponent(getVisitorId())}`, { headers: authHeader });
      if (r.ok) { const d = await r.json(); setComments(d.comments || []); setCommentCount(d.comment_count || 0); setLikes(d.likes || 0); setLiked(!!d.liked); }
    } catch { setComments([]); }
  };

  const toggleOpen = () => { const n = !open; setOpen(n); if (n && comments === null) loadComments(); };

  const toggleLike = async () => {
    if (liking) return; setLiking(true);
    try {
      const r = await fetch(`${API_URL}/api/news/${article.id}/like`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ visitor_id: getVisitorId() }),
      });
      if (r.ok) { const d = await r.json(); setLikes(d.likes); setLiked(d.liked); }
    } catch { /* noop */ }
    setLiking(false);
  };

  const submitComment = async () => {
    const text = content.trim();
    if (!text) { toast.error("댓글 내용을 입력해 주세요."); return; }
    if (!user && !nickname.trim()) { toast.error("닉네임을 입력해 주세요."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/news/${article.id}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ content: text, author_name: nickname.trim() }),
      });
      if (r.ok) {
        const d = await r.json();
        setComments((prev) => [...(prev || []), d.comment]);
        setCommentCount(d.comment_count);
        setContent("");
        toast.success("댓글이 등록되었습니다.");
      } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "댓글 등록 실패"); }
    } catch { toast.error("댓글 등록 실패"); }
    setSubmitting(false);
  };

  const deleteComment = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/news/${article.id}/comments/${id}`, { method: "DELETE", headers: authHeader });
      if (r.ok) { setComments((prev) => (prev || []).filter((c) => c.id !== id)); setCommentCount((c) => Math.max(0, c - 1)); }
      else toast.error("삭제 권한이 없습니다.");
    } catch { toast.error("삭제 실패"); }
  };

  return (
    <div className="mt-2" data-testid={`news-social-${article.id}`}>
      <div className="flex items-center gap-4">
        <button onClick={toggleLike} disabled={liking}
          className={`flex items-center gap-1 text-[12.5px] font-semibold transition-colors ${liked ? "text-rose-500" : "text-stone-400 hover:text-rose-400"}`}
          data-testid={`news-like-btn-${article.id}`}>
          <Heart className={`w-4 h-4 ${liked ? "fill-rose-500" : ""}`} /> <span data-testid={`news-like-count-${article.id}`}>{likes}</span>
        </button>
        <button onClick={toggleOpen}
          className={`flex items-center gap-1 text-[12.5px] font-semibold transition-colors ${open ? "text-burgundy-600" : "text-stone-400 hover:text-burgundy-500"}`}
          data-testid={`news-comment-toggle-${article.id}`}>
          <MessageCircle className="w-4 h-4" /> 댓글 <span data-testid={`news-comment-count-${article.id}`}>{commentCount}</span>
        </button>
      </div>

      {open && (
        <div className="mt-3 rounded-xl bg-stone-50 border border-stone-100 p-3" data-testid={`news-comment-box-${article.id}`}>
          <div className="space-y-3 mb-3">
            {comments === null ? (
              <p className="text-xs text-stone-400 text-center py-2">불러오는 중…</p>
            ) : comments.length === 0 ? (
              <p className="text-xs text-stone-400 text-center py-2">첫 댓글을 남겨보세요 💬</p>
            ) : comments.map((c) => (
              <div key={c.id} className="flex gap-2.5 group" data-testid={`news-comment-${c.id}`}>
                <div className="w-8 h-8 rounded-full bg-burgundy-100 text-burgundy-700 flex items-center justify-center font-bold text-xs shrink-0">{(c.author || "익")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-stone-800">{c.author || "익명"}</span>
                    {c.is_member && <span className="text-[10px] font-bold text-burgundy-600 bg-burgundy-50 px-1.5 py-0.5 rounded-full">회원</span>}
                    <span className="text-[11px] text-stone-400">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-[13px] text-stone-700 mt-0.5 whitespace-pre-wrap break-words">{c.content}</p>
                </div>
                {(user?.role === "admin" || (user && c.author_id === user.id)) && (
                  <button onClick={() => deleteComment(c.id)} className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-rose-500 shrink-0" data-testid={`news-comment-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {!user && (
              <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="닉네임" maxLength={20}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:border-burgundy-400" data-testid={`news-comment-nickname-${article.id}`} />
            )}
            <div className="flex gap-2">
              <input type="text" value={content} onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) submitComment(); }}
                placeholder="댓글을 남겨주세요" maxLength={1000}
                className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:border-burgundy-400" data-testid={`news-comment-input-${article.id}`} />
              <button onClick={submitComment} disabled={submitting}
                className="flex items-center gap-1 px-3.5 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-700 text-white text-[13px] font-bold disabled:opacity-50 shrink-0" data-testid={`news-comment-submit-${article.id}`}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} 등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewsRow({ a, onDeleted }) {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin";
  const title = a.korean_title || a.original_title;
  const highlight = a.is_pinned || a.is_featured;

  const handleDelete = async () => {
    if (!window.confirm("이 기사를 삭제할까요? 되돌릴 수 없습니다.")) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/news/${a.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) { toast.success("기사가 삭제되었습니다."); onDeleted?.(a.id); }
      else toast.error("삭제 권한이 없습니다.");
    } catch { toast.error("삭제에 실패했습니다."); }
  };

  return (
    <div className={`px-3 sm:px-4 py-3 ${highlight ? "border-l-2 border-burgundy-500" : ""}`} data-testid={`news-row-${a.id}`}>
      <div className="flex items-start gap-2">
        <a href={a.url} target="_blank" rel="noopener noreferrer" className="group block flex-1 min-w-0">
        {/* Desktop: 카테고리 15 | 제목 55 | 매체 15 | 날짜 15 */}
        <div className="hidden sm:flex sm:items-center gap-3">
          <div className="w-[15%] flex items-center gap-1.5 min-w-0">
            {highlight && <span className="shrink-0 text-[10px] font-bold text-white bg-burgundy-600 rounded px-1.5 py-0.5">주목</span>}
            <span className="truncate text-[12px] font-semibold text-burgundy-600">[{a.category}]</span>
          </div>
          <div className="w-[55%] min-w-0">
            <span className="block truncate text-[14px] font-semibold text-stone-800 group-hover:text-burgundy-700 group-hover:underline">{title}</span>
          </div>
          <div className="w-[15%] min-w-0 text-right">
            <span className="truncate text-[12px] text-stone-400">{a.source_name}</span>
          </div>
          <div className="w-[15%] flex items-center justify-end gap-1.5">
            <span className="text-[12px] text-stone-400 whitespace-nowrap">{fmtNewsDate(a.published_date)}</span>
            <ExternalLink className="w-3.5 h-3.5 text-stone-300 group-hover:text-burgundy-500 shrink-0" />
          </div>
        </div>
        {/* Mobile: 2줄 */}
        <div className="sm:hidden">
          <div className="flex items-start gap-1.5">
            {highlight && <span className="shrink-0 mt-0.5 text-[10px] font-bold text-white bg-burgundy-600 rounded px-1.5 py-0.5">주목</span>}
            <span className="shrink-0 text-[12px] font-semibold text-burgundy-600">[{a.category}]</span>
            <span className="text-[14px] font-semibold text-stone-800 line-clamp-2 group-hover:text-burgundy-700">{title}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-stone-400">
            <span className="truncate">{a.source_name}</span><span>·</span>
            <span className="whitespace-nowrap">{fmtNewsDate(a.published_date)}</span>
            <ExternalLink className="w-3.5 h-3.5 ml-auto text-stone-300 group-hover:text-burgundy-500" />
          </div>
        </div>
        </a>
        {isAdmin && (
          <button
            onClick={handleDelete}
            className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] font-semibold text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
            data-testid={`news-admin-delete-${a.id}`}
            title="관리자 삭제"
          >
            <Trash2 className="w-3.5 h-3.5" /> 삭제
          </button>
        )}
      </div>
      <NewsSocial article={a} />
    </div>
  );
}

export default function NewsPage() {
  const { token } = useAuth();
  const [category, setCategory] = useState("전체");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (reset) => {
    const nextSkip = reset ? 0 : skip;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ skip: String(nextSkip), limit: String(PAGE_SIZE), visitor_id: getVisitorId() });
      if (category && category !== "전체") params.set("category", category);
      if (query) params.set("search", query);
      const r = await fetch(`${API_URL}/api/news?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = await r.json();
      const items = d.articles || [];
      setTotal(d.total || 0);
      setArticles((prev) => (reset ? items : [...prev, ...items]));
      setSkip(nextSkip + items.length);
    } catch {
      if (reset) setArticles([]);
    }
    setLoading(false); setLoadingMore(false);
  }, [category, query, skip, token]);

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [category, query, token]);

  const hasMore = articles.length < total;

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-burgundy-600" strokeWidth={2.2} />
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">중년싱글 뉴스 &amp; 트렌드</h1>
          </div>
          <p className="mt-2 text-sm text-stone-500">
            국내외 뉴스와 미디어를 통해 만나는 중년 싱글의 사랑, 관계 그리고 새로운 라이프스타일
          </p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) setQuery(search.trim()); }}
              placeholder="기사 제목·매체명 검색"
              className="w-full pl-9 pr-24 py-2.5 rounded-full border border-stone-200 bg-white text-sm focus:outline-none focus:border-burgundy-400"
              data-testid="news-search-input"
            />
            <button
              onClick={() => setQuery(search.trim())}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-xs font-bold"
              data-testid="news-search-btn"
            >
              검색
            </button>
          </div>
        </div>

        {/* Category filters */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="news-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${
                category === c ? "bg-burgundy-600 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-burgundy-300"
              }`}
              data-testid={`news-filter-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-burgundy-400" /></div>
          ) : articles.length === 0 ? (
            <div className="py-12 text-center text-sm text-stone-400" data-testid="news-empty">
              조건에 맞는 기사가 없습니다.
            </div>
          ) : (
            articles.map((a) => <NewsRow key={a.id} a={a} onDeleted={(id) => { setArticles((prev) => prev.filter((x) => x.id !== id)); setTotal((t) => Math.max(0, t - 1)); }} />)
          )}
        </div>

        {/* Load more */}
        {!loading && hasMore && (
          <div className="mt-5 flex justify-center">
            <button
              onClick={() => load(false)}
              disabled={loadingMore}
              className="px-6 py-2.5 rounded-full bg-white border border-stone-300 text-stone-700 text-sm font-semibold hover:border-burgundy-300 disabled:opacity-50"
              data-testid="news-load-more"
            >
              {loadingMore ? "불러오는 중…" : "더보기"}
            </button>
          </div>
        )}
        {!loading && articles.length > 0 && (
          <p className="mt-3 text-center text-xs text-stone-400">전체 {total}건 중 {articles.length}건 표시</p>
        )}
      </div>
    </div>
  );
}
